/**
 * Utilitário de Mensagens de Erro Amigáveis
 * 
 * Centraliza mensagens de erro traduzidas e amigáveis para o usuário,
 * baseadas em códigos de status HTTP e tipos de erro.
 * 
 * @module utils/errorMessages
 */

/**
 * Mapeamento de códigos de status HTTP para mensagens amigáveis
 */
const HTTP_ERROR_MESSAGES = {
    400: 'Os dados enviados são inválidos. Verifique as informações e tente novamente.',
    401: 'Sua sessão expirou. Por favor, faça login novamente.',
    403: 'Você não tem permissão para acessar este recurso.',
    404: 'O recurso solicitado não foi encontrado.',
    408: 'A requisição demorou muito para responder. Verifique sua conexão e tente novamente.',
    409: 'Já existe um registro com essas informações.',
    422: 'Os dados enviados não são válidos. Verifique os campos e tente novamente.',
    429: 'Muitas requisições. Por favor, aguarde um momento e tente novamente.',
    500: 'Erro interno do servidor. Nossa equipe foi notificada e está trabalhando para resolver.',
    502: 'Servidor temporariamente indisponível. Tente novamente em alguns instantes.',
    503: 'Serviço temporariamente indisponível. Estamos realizando manutenção.',
    504: 'O servidor demorou muito para responder. Tente novamente.',
};

/**
 * Mapeamento de tipos de erro de rede para mensagens amigáveis
 */
const NETWORK_ERROR_MESSAGES = {
    ERR_NETWORK: 'Tente daqui a alguns segundos.',
    ERR_TIMEOUT: 'A requisição demorou muito. Tente daqui a alguns segundos.',
    ERR_CANCELED: 'A requisição foi cancelada.',
    ERR_CONNECTION_REFUSED: 'Não foi possível conectar ao servidor. Tente daqui a alguns segundos.',
    ERR_INTERNET_DISCONNECTED: 'Sem conexão com a internet. Verifique sua conexão e tente novamente.',
};

/**
 * Mensagens genéricas por contexto
 */
const CONTEXT_MESSAGES = {
    dashboard: {
        summary: 'Não foi possível carregar o resumo do dashboard. Tente recarregar a página.',
        taskDistribution: 'Não foi possível carregar a distribuição de tarefas.',
        workload: 'Não foi possível carregar a carga de trabalho dos profissionais.',
        equipment: 'Não foi possível carregar o status dos equipamentos.',
        pontoDesvios: 'Não foi possível carregar os desvios de ponto.',
        estoqueAlertas: 'Não foi possível carregar os alertas de estoque.',
    },
    generic: {
        load: 'Não foi possível carregar os dados. Tente novamente.',
        save: 'Não foi possível salvar os dados. Verifique as informações e tente novamente.',
        delete: 'Não foi possível excluir o item. Tente novamente.',
        update: 'Não foi possível atualizar os dados. Tente novamente.',
        unknown: 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.',
    },
};

/**
 * Obtém mensagem de erro amigável baseada no código de status HTTP
 * 
 * @param {number} status - Código de status HTTP
 * @returns {string} Mensagem amigável
 */
export const getHttpErrorMessage = (status) => {
    return HTTP_ERROR_MESSAGES[status] || HTTP_ERROR_MESSAGES[500];
};

/**
 * Obtém mensagem de erro amigável baseada no tipo de erro de rede
 * 
 * @param {string} code - Código do erro de rede
 * @returns {string} Mensagem amigável
 */
export const getNetworkErrorMessage = (code) => {
    return NETWORK_ERROR_MESSAGES[code] || NETWORK_ERROR_MESSAGES.ERR_NETWORK;
};

/**
 * Obtém mensagem de erro amigável baseada no contexto
 * 
 * @param {string} context - Contexto do erro (ex: 'dashboard', 'generic')
 * @param {string} action - Ação que falhou (ex: 'summary', 'load', 'save')
 * @returns {string} Mensagem amigável
 */
export const getContextErrorMessage = (context, action) => {
    const contextMessages = CONTEXT_MESSAGES[context];
    if (!contextMessages) {
        return CONTEXT_MESSAGES.generic.unknown;
    }
    return contextMessages[action] || CONTEXT_MESSAGES.generic.unknown;
};

/**
 * Extrai mensagem de erro amigável de um objeto de erro
 * 
 * @param {Error} error - Objeto de erro
 * @param {object} options - Opções
 * @param {string} options.context - Contexto do erro (ex: 'dashboard')
 * @param {string} options.action - Ação que falhou (ex: 'summary')
 * @param {string} options.fallback - Mensagem de fallback personalizada
 * @returns {string} Mensagem amigável
 * 
 * @example
 * ```javascript
 * try {
 *     await api.get('/dashboard/summary');
 * } catch (error) {
 *     const message = getErrorMessage(error, {
 *         context: 'dashboard',
 *         action: 'summary',
 *     });
 *     console.error(message);
 * }
 * ```
 */
export const getErrorMessage = (error, options = {}) => {
    const { context, action, fallback } = options;

    // Se uma mensagem personalizada foi fornecida, usa ela
    if (fallback) {
        return fallback;
    }

    // Se há uma mensagem do servidor, usa ela (se for amigável)
    if (error?.response?.data?.message) {
        const serverMessage = error.response.data.message;
        // Se a mensagem parece técnica, tenta melhorar
        if (serverMessage.includes('Error') || serverMessage.includes('Exception')) {
            // Usa mensagem baseada no status HTTP
            const status = error.response.status;
            return getHttpErrorMessage(status);
        }
        return serverMessage;
    }

    // Se é um erro de rede
    if (error?.code && NETWORK_ERROR_MESSAGES[error.code]) {
        return getNetworkErrorMessage(error.code);
    }

    // Se é um erro de rede genérico
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
        return getNetworkErrorMessage('ERR_NETWORK');
    }

    // Se há um código de status HTTP, usa mensagem baseada nele
    if (error?.response?.status) {
        return getHttpErrorMessage(error.response.status);
    }

    // Se há contexto e ação, usa mensagem baseada neles
    if (context && action) {
        return getContextErrorMessage(context, action);
    }

    // Fallback genérico
    return CONTEXT_MESSAGES.generic.unknown;
};

/**
 * Verifica se um erro é recuperável (pode ser tentado novamente)
 * 
 * @param {Error} error - Objeto de erro
 * @returns {boolean} Se o erro é recuperável
 */
export const isRecoverableError = (error) => {
    // Erros de rede são recuperáveis
    if (error?.code && NETWORK_ERROR_MESSAGES[error.code]) {
        return true;
    }

    // Erros 5xx são recuperáveis (erros do servidor)
    if (error?.response?.status >= 500 && error?.response?.status < 600) {
        return true;
    }

    // Erros 408, 429 são recuperáveis (timeout, rate limit)
    if (error?.response?.status === 408 || error?.response?.status === 429) {
        return true;
    }

    // Erros 4xx (exceto 401, 403) podem ser recuperáveis se o usuário corrigir
    if (error?.response?.status >= 400 && error?.response?.status < 500) {
        const nonRecoverableStatuses = [401, 403];
        return !nonRecoverableStatuses.includes(error.response.status);
    }

    return false;
};

/**
 * Verifica se um erro requer autenticação
 * 
 * @param {Error} error - Objeto de erro
 * @returns {boolean} Se o erro requer autenticação
 */
export const requiresAuthentication = (error) => {
    return error?.response?.status === 401 || error?.response?.status === 403;
};

export default {
    getHttpErrorMessage,
    getNetworkErrorMessage,
    getContextErrorMessage,
    getErrorMessage,
    isRecoverableError,
    requiresAuthentication,
};

